import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import styled from '@emotion/styled'

import uniqid from 'uniqid'

import { Tooltip, TooltipUtils } from '../tooltip'
import { IconQuestion, IconFATimes } from '../SVGIcons'
import { Link, LINK_VARIANT_DEFAULT_NEGATIVE } from '../link'

export const TRUSTED_EMAIL_INPUT_VARIANT_NEUTRAL = 'neutral'
export const TRUSTED_EMAIL_INPUT_VARIANT_INVALID = 'invalid'
export const TRUSTED_EMAIL_INPUT_VARIANT_SUCCESS = 'success'
export const TRUSTED_EMAIL_INPUT_VARIANT_UNVERIFIED = 'unverified'
export const TRUSTED_EMAIL_INPUT_VARIANT_ERROR = 'error'
export const TRUSTED_EMAIL_INPUT_VARIANT_WARNING = 'warning'
export const TRUSTED_EMAIL_INPUT_VARIANT_BLOCKED = 'blocked'
export const TRUSTED_EMAIL_INPUT_VARIANT_INFO = 'info'

const renderInputVariant = variant => {
  switch (variant) {
    case TRUSTED_EMAIL_INPUT_VARIANT_NEUTRAL:
      return `border-color: var(--geyser) !important; `
    case TRUSTED_EMAIL_INPUT_VARIANT_SUCCESS:
      return `border-color: #6DC33A !important;`
    case TRUSTED_EMAIL_INPUT_VARIANT_INVALID:
    case TRUSTED_EMAIL_INPUT_VARIANT_UNVERIFIED:
    case TRUSTED_EMAIL_INPUT_VARIANT_BLOCKED:
    case TRUSTED_EMAIL_INPUT_VARIANT_ERROR:
      return `border-color: var(--sunset-orange) !important;`
    case TRUSTED_EMAIL_INPUT_VARIANT_WARNING:
      return `border-color: #b5b9be !important;`
    case TRUSTED_EMAIL_INPUT_VARIANT_INFO:
      return `border-color: #3dc6f5 !important;`
  }
}

const renderHintContainerVariant = variant => {
  switch (variant) {
    case TRUSTED_EMAIL_INPUT_VARIANT_INVALID:
      return `color: var(--sunset-orange);
      padding-top: 8px;`
    case TRUSTED_EMAIL_INPUT_VARIANT_WARNING:
      return `color: var(--boulder);
      
        background-color: #EEEEEE;
        border: 1px solid #ccc;
        padding: 7px 10px 8px;
        border-bottom-right-radius: 4px;
        border-bottom-left-radius: 4px;
      `
    case TRUSTED_EMAIL_INPUT_VARIANT_UNVERIFIED:
    case TRUSTED_EMAIL_INPUT_VARIANT_ERROR:
    case TRUSTED_EMAIL_INPUT_VARIANT_BLOCKED:
      return `color: var(--boulder);
      
        background-color: #fb5959;
        color: var(--pure-white);
        padding: 7px 10px 8px;
        border-bottom-right-radius: 4px;
        border-bottom-left-radius: 4px;
      `

    case TRUSTED_EMAIL_INPUT_VARIANT_INFO:
        return `color: var(--boulder);
      
        background-color: #3dc6f5;
        color: var(--pure-white);
        padding: 7px 10px 8px;
        border-bottom-right-radius: 4px;
        border-bottom-left-radius: 4px;
      `
  }
}

const renderTooltipIconVariant = variant => {
  switch (variant) {
    case TRUSTED_EMAIL_INPUT_VARIANT_WARNING:
      return `opacity: 0.4;`
  }
}

const StyledContainer = styled.div``

const StyledInput = styled.input`
  box-sizing: border-box;
  display: inline-block;
  width: 100%;
  height: 36px;

  border-style: solid;
  border-width: 1px;
  border-radius: 4px;

  padding: 0 8px;
  font-size: 15px;
  line-height: 36px;
  color: var(--tundora);
  background-color: var(--pure-white);

  transition: all 0.3s ease;

  ${({ variant }) => renderInputVariant(variant)}

  &:focus {
    outline: none;
  }
`

const StyledHintContainer = styled.div`
  display: flex;
  align-items: center;

  position: relative;
  top: -4px;

  font-weight: 500;
  font-size: 13px;
  padding-top: 4px;
  letter-spacing: 0.03em;

  a:hover {
    color: var(--mine-shaft);
  }

  ${({ variant }) => renderHintContainerVariant(variant)}
`

const StyledHint = styled.span`
  padding-right: 0.3rem;
`

const StyledTooltipIcon = styled.span`
  cursor: pointer;
  position: relative;
  top: 2px;
  ${({ variant }) => renderTooltipIconVariant(variant)}
`

const StyledDismiss = styled.a`
  cursor: pointer;
  color: var(--boulder);
  opacity: 0.3;
  font-size: 13px;
  display: flex;
  align-self: flex-start;
  padding-top: 4px;
`

const StyledSpacer = styled.div`
  flex-grow: 1;
`

const StyledTooltipContainer = styled.span`
  font-weight: 400;
  margin-left: 8px;
`

const variantsWithNotes = [
  TRUSTED_EMAIL_INPUT_VARIANT_INVALID,
  TRUSTED_EMAIL_INPUT_VARIANT_WARNING,
  TRUSTED_EMAIL_INPUT_VARIANT_UNVERIFIED,
  TRUSTED_EMAIL_INPUT_VARIANT_ERROR,
  TRUSTED_EMAIL_INPUT_VARIANT_BLOCKED,
  TRUSTED_EMAIL_INPUT_VARIANT_INFO,

]

export const TrustedEmailInput = ({
  id,
  className,
  value,
  placeholder,
  onChange,
  onDismissWarningClick,
  onVerifyClick,
  dataAttributeTest,
  variant,
  i18n,
  required,
  onKeyPress,
  disabled
}) => {
  const { current: inputUuid } = useRef(id || uniqid('trusted-email-input-'))
  const tooltipUuid = 'tooltip_' + inputUuid

  useEffect(() => {
    TooltipUtils.rebuild()
  }, [i18n, variant])

  return (
    <StyledContainer
      className={className}
      data-mu-test={classnames('input-trusted-sender', dataAttributeTest)}
    >
      <StyledInput
        id={inputUuid}
        type='text'
        maxLength={500}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        variant={variant}
        required={required}
        onKeyPress={onKeyPress}
        disabled={disabled}
      />
      {variantsWithNotes.includes(variant) && (
        <StyledHintContainer variant={variant}>
          {variant === TRUSTED_EMAIL_INPUT_VARIANT_INVALID && (
            <StyledHint>{i18n.invalid}</StyledHint>
          )}
          {variant === TRUSTED_EMAIL_INPUT_VARIANT_WARNING && (
            <>
              <StyledHint>
                {i18n.warning}
                <StyledTooltipContainer>
                  <Tooltip
                    uuid={`trusted-email-input-tooltip-${tooltipUuid}`}
                    id={tooltipUuid}
                  >
                    {/* {i18n.warningTooltip} */}
                  </Tooltip>
                  <StyledTooltipIcon
                    variant={variant}
                    data-tip={i18n.warningTooltip}
                    data-for={tooltipUuid}
                  >
                    <IconQuestion />
                  </StyledTooltipIcon>
                </StyledTooltipContainer>
              </StyledHint>

              <StyledSpacer />
              <StyledDismiss
                onClick={onDismissWarningClick}
                data-mu-test='dismiss'
              >
                <IconFATimes />
              </StyledDismiss>
            </>
          )}
          {variant === TRUSTED_EMAIL_INPUT_VARIANT_UNVERIFIED && (
            <>
              <StyledHint>{i18n.unverified}</StyledHint>
              <Link
                variant={LINK_VARIANT_DEFAULT_NEGATIVE}
                onClick={onVerifyClick}
                data-mu-test='verify'
              >
                {i18n.verifyLink}
              </Link>
            </>
          )}
          {variant === TRUSTED_EMAIL_INPUT_VARIANT_ERROR && (
            <>
              <StyledHint>
                {i18n.error}
                <StyledTooltipContainer>
                  <Tooltip
                    uuid={`trusted-email-input-tooltip-${tooltipUuid}`}
                    id={tooltipUuid}
                  >
                    {/* {i18n.errorTooltip} */}
                  </Tooltip>
                  <StyledTooltipIcon
                    data-for={tooltipUuid}
                    data-tip={i18n.errorTooltip}
                  >
                    <IconQuestion />
                  </StyledTooltipIcon>
                </StyledTooltipContainer>
              </StyledHint>
            </>
          )}
          {variant === TRUSTED_EMAIL_INPUT_VARIANT_INFO && (
            <>
            <StyledHint>
              {i18n.info}
              <StyledTooltipContainer>
                <Tooltip
                  uuid={`trusted-email-input-tooltip-${tooltipUuid}`}
                  id={tooltipUuid}
                >
                  {/* {i18n.errorTooltip} */}
                </Tooltip>
                <StyledTooltipIcon
                  data-for={tooltipUuid}
                  data-tip={i18n.infoTooltip}
                >
                  <IconQuestion />
                </StyledTooltipIcon>
              </StyledTooltipContainer>
            </StyledHint>
          </>
          )}
          {variant === TRUSTED_EMAIL_INPUT_VARIANT_BLOCKED && (
            <StyledHint>{i18n.blocked}</StyledHint>
          )}
        </StyledHintContainer>
      )}
    </StyledContainer>
  )
}

TrustedEmailInput.defaultProps = {
  i18n: {
    invalid: 'Oops! The format is not valid',
    unverified: 'Unverified',
    verifyLink: 'Verify now',
    error: 'We found a potentially serious deliverability issue.',
    errorTooltip:
      'We ran some tests and found one or more potentially serious..',
    warning: 'You can improve your deliverability.',
    warningTooltip:
      'We ran some tests and found that there might be ways for you to improve...',
    blocked:
      'Unfortunately the email address you entered has been blacklisted and cannot be used.',
      info: "into text",
      infoTooltip: "info tolltip"
  }
}

TrustedEmailInput.propTypes = {
  id: PropTypes.string,
  
  className: PropTypes.string,
  dataAttributeTest: PropTypes.string,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onDismissWarningClick: PropTypes.func,
  onVerifyClick: PropTypes.func,
  variant: PropTypes.oneOf([
    TRUSTED_EMAIL_INPUT_VARIANT_NEUTRAL,
    TRUSTED_EMAIL_INPUT_VARIANT_INVALID,
    TRUSTED_EMAIL_INPUT_VARIANT_SUCCESS,
    TRUSTED_EMAIL_INPUT_VARIANT_UNVERIFIED,
    TRUSTED_EMAIL_INPUT_VARIANT_ERROR,
    TRUSTED_EMAIL_INPUT_VARIANT_WARNING,
    TRUSTED_EMAIL_INPUT_VARIANT_BLOCKED,
    TRUSTED_EMAIL_INPUT_VARIANT_INFO
  ]),
  i18n: PropTypes.shape({
    invalid: PropTypes.string,
    unverified: PropTypes.string,
    verifyLink: PropTypes.string,
    error: PropTypes.string,
    errorTooltip: PropTypes.node,
    warning: PropTypes.string,
    warningTooltip: PropTypes.node,
    blocked: PropTypes.string
  }),

}
